import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, HostListener,
    Input, OnChanges, Output, SimpleChanges
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
    ArmTaskFollowUpInput, AttachmentDto, CancelTaskFollowUpInput, EmailAutomationServiceProxy, EmailBodyDto,
    ExtendTaskFollowUpInput, TaskEmailDto, TaskThreadDto, ThreadMessageDto
} from '@shared/service-proxies/service-proxies';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';

declare const abp: any;

export type EmailReplyMode = 'reply' | 'replyAll' | 'forward';

export interface EmailReplyIntent {
    mode: EmailReplyMode;
    email: TaskEmailDto;
    /** The mailbox the reply is sent from (shared address or the owner's email) - shown in the composer's From row. */
    inboxAddress?: string;
    /** Story 6: opened via the no-reply banner's "Draft follow-up" - the composer pre-loads a follow-up
     *  nudge into its Draft-with-AI popup so one tap writes the chase. */
    nudge?: boolean;
}

/**
 * Story 6: one rendered no-reply silence event (a FIRED follow-up row) in the thread. isActive ones show
 * the amber action banner at the top of the thread; all fired events (active or resolved) leave a chip
 * anchored above the outbound message they guard, persisting as history with a resolution suffix.
 */
export interface FollowUpEventVm {
    id: string;
    isActive: boolean;
    chipText: string;        // "5 days, no reply"
    chipSuffix: string;      // " · dismissed" | " · followed up Jul 15" | ''
    bannerTitle: string;     // "No reply from X"
    bannerSubtitle: string;  // "Your last email was sent Jul 15 — 5 days ago."
    anchorMessageId: string | undefined;
}

/**
 * Email surface for an email-origin task (edit-task modal left panel). The thread IS the task body
 * (spec v2 sec 18): a toolbar (Reply / Reply all / Forward + View original + Open in Outlook) over the
 * full conversation rendered newest-first - each message shows its direction (Received/Sent), a "Latest"
 * badge on the newest, an expandable Details block (From/To/Cc/Inbox/Date) and its body in a sandboxed
 * iframe (full fidelity - the tiptap notes editor can't render email HTML/table layouts).
 *
 * Nothing is stored: the header is GetTaskEmail (drives the Reply intent the host needs) and the thread
 * is GetTaskThread, both live by immutable id / conversation id. We gate on the PRESENCE of a linked email
 * (GetTaskEmail.hasEmail) - NOT on taskType, since an email task can carry a non-Email activity type.
 */
@Component({
    selector: 'vf-task-email-bar',
    templateUrl: './task-email-bar.component.html',
    styleUrls: ['./task-email-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskEmailBarComponent implements OnChanges {
    @Input() taskId: number | undefined;
    @Input() taskType: number | undefined;
    /** The task's notes HTML; the AI/firm summary the classifier wrote is read out of it for the summary card. */
    @Input() taskNotes: string | undefined;
    /**
     * Whether the linked contact has an email on file (Story 5). EXACTLY false shows a small amber warn
     * glyph inside the Reply button (the button stays enabled - the composer hosts the fix flow);
     * true or undefined (no contact / unknown) shows nothing.
     */
    @Input() contactHasEmail: boolean | undefined;

    @Output() reply = new EventEmitter<EmailReplyIntent>();
    /** Fires when the Task-notes card is revealed, so the host can refresh the (Syncfusion) editor's layout. */
    @Output() notesShown = new EventEmitter<void>();
    /**
     * Fires when we know whether the task has a linked email. The host renders the notes editor INSIDE the
     * email surface (this bar) when true, or directly in the panel (original layout) when false - so a plain
     * task's editor is untouched by the email surface.
     */
    @Output() hasEmailChange = new EventEmitter<boolean>();
    /** "View in folder" → the host opens Trovve's in-app navigate-sub-folder modal (same as the Files tab). */
    @Output() openFolderInApp = new EventEmitter<{ keyId: string; driveId: string; siteId: string; name: string; url: string }>();
    /** No-reply banner "Confirm & complete": the watcher has already been rescheduled with the picked Wait
     *  timing; the host now completes the task via its EXISTING mark-complete path (unchanged). */
    @Output() completeTask = new EventEmitter<void>();
    /** The user snoozed the fired follow-up via the "Wait" dropdown. The host closes the task modal so the
     *  snoozed task leaves the view — consistent with the grid Snooze, which drops the row off Today. */
    @Output() followUpSnoozed = new EventEmitter<void>();

    // The bar only takes panel space (flex-fills) when it actually shows the email surface.
    @HostBinding('class.is-email') get hostIsEmail(): boolean {
        return this.hasEmail;
    }

    // The classifier-written summary, extracted from the notes (no extra AI call - it was generated at create).
    summaryText = '';

    // Notes-on-top (sec 18): the task notes live in a collapsible card at the top of the thread scroll,
    // behind an "Add notes" toggle in the action bar. The notes editor itself is projected in (<ng-content>)
    // from the host modal so all its wiring (save / write-with-AI / proofread) is unchanged.
    notesOpen = false;
    notesCollapsed = false;
    /** Set when the user explicitly closes the notes card for the current task, so an async
     *  taskNotes (description) update doesn't auto-reopen it against their intent. Reset per task. */
    private notesUserClosed = false;

    email: TaskEmailDto | undefined;
    loading = false;
    expanded = false;
    menuOpen = false;

    // Thread (sec 18): the whole conversation, newest-first. Bodies are rendered per-message in sandboxed
    // iframes; details/body visibility is tracked per message id so each row expands independently.
    thread: TaskThreadDto | undefined;
    threadLoading = false;
    threadError = '';
    // Load-earlier (sec 18.2): the backend returns the newest `threadTop` of the full conversation. A bigger
    // top reveals earlier messages; the meta line + button key off the true total it reports (HasMore).
    threadTop = 15;
    loadingMore = false;
    // The attachment whose bytes are being fetched (spinner/disable on that chip); one at a time.
    attLoadingId: string | undefined;
    // Attachment content types safe to PREVIEW inline (browser-native). Everything else is force-downloaded so
    // untrusted html/svg/xhtml can never execute in the app origin. Compared against the base type (no ;charset).
    private static readonly InlinePreviewTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];
    private readonly detailsOpen = new Set<string>();
    private readonly bodyHtml: { [id: string]: SafeHtml } = {};

    // "View original email" reading view (legacy single-message reader, still reachable from the toolbar):
    // the original body is fetched live and rendered in a sandboxed iframe.
    showOriginal = false;
    originalLoading = false;
    originalError = '';
    originalHtml: SafeHtml | undefined;
    private originalLoadedForTaskId: number | undefined;

    // Story 6: no-reply silence events rendered in the thread (fired follow-up rows). Rebuilt on each
    // thread load. activeBanner is the single unresolved fired event (amber action card at the top);
    // eventChipsByMsgId groups every fired event's chip under the outbound message it guards.
    activeBanner: FollowUpEventVm | undefined;
    eventChipsByMsgId: { [id: string]: FollowUpEventVm[] } = {};
    followUpSaving = false;
    private followUpMsgIds = new Set<string>();
    private newReplyMsgId: string | undefined;

    // Snooze ("Wait…") dropdown on the fired banner: preset options + a "Custom date" item that reveals an
    // inline date picker in the actions row (between Wait and Dismiss).
    waitMenuOpen = false;
    waitCustomOpen = false;
    /** The snooze picked from the menu ('3d'|'5d'|'1w'|'2w'|'custom'); '' = nothing picked (fresh banner). */
    waitSelected = '';
    /** The date chosen in the inline custom picker. */
    waitCustomDate: moment.Moment | undefined;
    /** Confirm step: after a wait is picked the amber alert flips to the blue "We'll wait for X" card
     *  (Confirm & complete / Change) - the wait is only applied + the task completed on Confirm. */
    waitConfirmMode = false;
    /** The resulting fire date shown in the confirm card ("...hasn't replied by Jul 25..."). */
    waitConfirmDateLabel = '';
    /** The first name shown in the confirm card ("We'll wait for Karen's reply") - snapshot at pick time. */
    waitConfirmWho = '';
    /** Min selectable date for the custom-date vf-date-picker; recomputed on each menu open
     *  (refreshWaitPresetVms) so a long-lived modal opened across midnight still bounds at today. */
    waitMinDate = moment().startOf('day');
    /** Mirrors AppComponentBase.toastrOptions (this component does NOT extend it). The app's CustomToastComponent
     *  only renders the message when enableHtml is set and positions via toast-bottom-full-width, so a bare
     *  this._toastr.success(msg) with no options produces an empty/offscreen toast. */
    private readonly toastrOptions: Partial<any> = {
        closeButton: false,
        debug: false,
        newestOnTop: false,
        progressBar: false,
        toastClass: 'toast',
        positionClass: 'toast-bottom-full-width',
        preventDuplicates: true,
        onclick: null,
        showDuration: '5000',
        hideDuration: '1000',
        timeOut: '5000',
        extendedTimeOut: '1000',
        showEasing: 'swing',
        hideEasing: 'linear',
        showMethod: 'fadeIn',
        hideMethod: 'fadeOut',
        tapToDismiss: false,
        enableHtml: true,
        maxOpened: 1
    };
    private waitPresets: { label: string; value: string; days: number }[] = [
        { label: '3 days', value: '3d', days: 3 },
        { label: '5 days', value: '5d', days: 5 },
        { label: '1 week', value: '1w', days: 7 },
        { label: '2 weeks', value: '2w', days: 14 }
    ];
    /** Menu rows with the resulting fire date pre-formatted (e.g. "5 days · Jul 25") - no template logic. */
    waitPresetVms = this.waitPresets.map(p => ({
        label: p.label,
        value: p.value,
        dateLabel: moment().add(p.days, 'days').format('MMM D')
    }));

    constructor(
        private _emailService: EmailAutomationServiceProxy,
        private _sanitizer: DomSanitizer,
        private _cdr: ChangeDetectorRef,
        private _toastr: ToastrService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.taskId || changes.taskType) {
            this.email = undefined;
            this.thread = undefined;
            this.threadError = '';
            this.expanded = false;
            this.menuOpen = false;
            this.showOriginal = false;
            this.originalHtml = undefined;
            this.originalError = '';
            this.originalLoadedForTaskId = undefined;
            this.notesOpen = false;
            this.notesCollapsed = false;
            this.notesUserClosed = false; // new task -> forget any prior explicit close
            this.activeBanner = undefined;
            this.eventChipsByMsgId = {};
            this.followUpMsgIds = new Set<string>();
            this.newReplyMsgId = undefined;
            this.followUpSaving = false; // a pending Wait/Dismiss on the previous task must not disable the new one's banner
            this.waitMenuOpen = false;
            this.waitCustomOpen = false;
            this.waitSelected = '';
            this.waitCustomDate = undefined;
            this.waitConfirmMode = false;
            this.waitConfirmDateLabel = '';
            this.waitConfirmWho = '';
            this.detailsOpen.clear();
            for (const key of Object.keys(this.bodyHtml)) {
                delete this.bodyHtml[key];
            }
            // Probe every task; GetTaskEmail returns hasEmail=false for non-email tasks and the surface stays
            // hidden. Gating on taskType would break it for email tasks carrying a non-Email activity type.
            // hasEmailChange is emitted from load() (async) so it never trips ExpressionChanged in the host.
            if (this.taskId) {
                this.load();
            }
        }
        if (changes.taskId || changes.taskNotes) {
            this.summaryText = this.extractSummary(this.taskNotes);
            // Keep the notes card open whenever the task carries notes content, so notes stay visible
            // on reopen — regardless of completion / reassignment. Respect an explicit user-close for
            // this task (guards against an async description load re-opening it after they dismissed it).
            if (!this.notesUserClosed && this.hasNotesContent(this.taskNotes)) {
                const wasHidden = !this.notesOpen || this.notesCollapsed;
                this.notesOpen = true;
                this.notesCollapsed = false;
                if (wasHidden) {
                    this.notesShown.emit(); // relayout the projected editor now that it's visible
                }
            }
        }
    }

    /** True when the notes/description HTML has any visible text (ignores empty <p></p> / whitespace). */
    private hasNotesContent(html: string | undefined): boolean {
        if (!html) {
            return false;
        }
        const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
        return text.length > 0;
    }

    /**
     * Pull the "Summary" section the classifier wrote into the notes (EmailTaskNotes.Build emits
     * `<p><strong>Summary</strong></p>` then a `<p>` with the text). Best-effort and read-only: parses the
     * HTML and returns the text content of the paragraph after the Summary heading; empty when absent or
     * the placeholder, so the card simply hides. No HTML is rendered from this - only textContent is used.
     */
    private extractSummary(html: string | undefined): string {
        if (!html) {
            return '';
        }
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const nodes = Array.from(doc.body.children);
            const headingIdx = nodes.findIndex(n => this.isHeadingNode(n) === 'summary');
            if (headingIdx < 0) {
                return '';
            }
            // Collect EVERYTHING between the "Summary" heading and the next section heading (Filed / Original
            // email / Replies) or the blank-line separator - regardless of node type. The classifier writes the
            // summary as one <p>, but the editor can normalise it into split <p>s or an <ol>, which a "next <p>"
            // grab would truncate (the reported "asking w" bug).
            const parts: string[] = [];
            for (let i = headingIdx + 1; i < nodes.length; i++) {
                const node = nodes[i];
                if (this.isHeadingNode(node)) {
                    break; // reached the next section
                }
                const text = (node.textContent || '').replace(/ /g, ' ').trim();
                if (!text) {
                    if (parts.length > 0) {
                        break; // blank separator after the summary body
                    }
                    continue; // skip a leading blank line
                }
                parts.push(text);
            }
            const summary = parts.join(' ').replace(/\s+/g, ' ').trim();
            return summary && summary.toLowerCase() !== 'no summary available.' ? summary : '';
        } catch {
            return '';
        }
    }

    /** A note section heading is a top-level `<p>` whose only content is a `<strong>` (e.g. Summary / Filed /
     *  Original email / Replies). Returns the lowercased heading text, or '' if the node isn't a heading. */
    private isHeadingNode(node: Element): string {
        if (!node || node.tagName !== 'P') {
            return '';
        }
        const strong = node.querySelector('strong');
        if (!strong) {
            return '';
        }
        const text = (node.textContent || '').trim();
        return text.length > 0 && text === (strong.textContent || '').trim() ? text.toLowerCase() : '';
    }

    get hasEmail(): boolean {
        return !!(this.email && this.email.hasEmail);
    }

    get senderName(): string {
        if (!this.email) {
            return '';
        }
        return this.email.fromName || this.email.from || '(unknown sender)';
    }

    get senderEmail(): string {
        return this.email && this.email.from ? '<' + this.email.from + '>' : '';
    }

    get messages(): ThreadMessageDto[] {
        return (this.thread && this.thread.messages) || [];
    }

    get hasMore(): boolean {
        return !!(this.thread && this.thread.hasMore);
    }

    get earlierCount(): number {
        const total = (this.thread && this.thread.totalCount) || 0;
        return Math.max(0, total - this.messages.length);
    }

    get inboxAddress(): string {
        return (this.thread && this.thread.inboxAddress) || '';
    }

    /** Show the "View in folder" button when we can open it - in-app (drive id) or as a SharePoint fallback (url).
     *  Thread-level (used by the attachment-too-large fallback); the per-message rows use canOpenMessageFolder. */
    get canOpenFolder(): boolean {
        return !!(this.thread && (this.thread.filedFolderDriveId || this.thread.filedFolderUrl));
    }

    openFolder(): void {
        if (!this.thread) {
            return;
        }
        // The host opens Trovve's navigate-sub-folder modal (drive id present) or falls back to SharePoint (url).
        this.openFolderInApp.emit({
            keyId: this.thread.filedFolderKeyId || '',
            driveId: this.thread.filedFolderDriveId || '',
            siteId: this.thread.filedFolderSiteId || '',
            name: this.thread.filedFolderName || 'Files',
            url: this.thread.filedFolderUrl || ''
        });
    }

    /** Can we open THIS message's filed folder (in-app via drive id, or the SharePoint url fallback)? */
    canOpenMessageFolder(m: ThreadMessageDto): boolean {
        return !!(m && (m.filedFolderDriveId || m.filedFolderUrl));
    }

    /** Open the per-email folder for THIS message (each email in the thread is filed to its own folder). */
    openMessageFolder(m: ThreadMessageDto): void {
        if (!m) {
            return;
        }
        this.openFolderInApp.emit({
            keyId: m.filedFolderKeyId || '',
            driveId: m.filedFolderDriveId || '',
            siteId: m.filedFolderSiteId || '',
            name: m.filedFolderName || 'Files',
            url: m.filedFolderUrl || ''
        });
    }

    trackByMsg(index: number, m: ThreadMessageDto): string | number {
        return m.id || index;
    }

    trackByAtt(index: number, att: AttachmentDto): string | number {
        return att.id || index;
    }

    messageSenderName(m: ThreadMessageDto): string {
        return m.fromName || m.from || '(unknown sender)';
    }

    messageInitial(m: ThreadMessageDto): string {
        const name = this.messageSenderName(m).trim();
        return name ? name.charAt(0).toUpperCase() : '?';
    }

    isDetailsOpen(m: ThreadMessageDto): boolean {
        return !!m.id && this.detailsOpen.has(m.id);
    }

    bodyDoc(m: ThreadMessageDto): SafeHtml | undefined {
        return m.id ? this.bodyHtml[m.id] : undefined;
    }

    toggleDetails(m: ThreadMessageDto, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        if (!m.id) {
            return;
        }
        if (this.detailsOpen.has(m.id)) {
            this.detailsOpen.delete(m.id);
        } else {
            this.detailsOpen.add(m.id);
        }
    }

    toggleExpand(): void {
        this.expanded = !this.expanded;
    }

    get notesBtnLabel(): string {
        return this.notesOpen ? 'Notes' : 'Add notes';
    }

    toggleNotes(): void {
        this.notesOpen = !this.notesOpen;
        this.notesUserClosed = !this.notesOpen; // remember an explicit close so auto-open won't fight it
        if (this.notesOpen) {
            this.notesCollapsed = false;
            this.notesShown.emit();
        }
    }

    toggleNotesCollapse(): void {
        this.notesCollapsed = !this.notesCollapsed;
        if (!this.notesCollapsed) {
            this.notesShown.emit();
        }
    }

    closeNotes(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        this.notesOpen = false;
        this.notesUserClosed = true; // explicit close -> don't auto-reopen on a later notes update
    }

    /**
     * Reveal the Task-notes card (open + expanded). Called by the host when content is written into the
     * notes editor while the card is hidden/collapsed - e.g. "Copy link to notes" on a file upload/create -
     * so the inserted link is actually visible. Emits notesShown (to relayout the editor) only when it was
     * previously hidden; a no-op when already open and expanded.
     */
    showNotes(): void {
        const wasHidden = !this.notesOpen || this.notesCollapsed;
        this.notesOpen = true;
        this.notesCollapsed = false;
        this.notesUserClosed = false; // an explicit "show" clears any remembered close
        if (wasHidden) {
            this.notesShown.emit();
        }
    }

    get participantCount(): number {
        return (this.thread && this.thread.participantCount) || 0;
    }

    get lastActivity(): Date | undefined {
        const d = this.thread && this.thread.lastActivity;
        return d ? new Date(d as any) : undefined;
    }

    /** Human-brief bullets under the lead-in (server summary v2). Empty list = lead-in only. */
    get summaryBullets(): string[] {
        return (this.thread && this.thread.aiThreadSummaryBullets) || [];
    }

    trackByBullet(index: number): number {
        return index;
    }

    /** 'direct' | 'form' | 'referral' (Story 4). The header DTO loads first; the thread mirrors it. */
    private get subType(): string {
        return ((this.email && this.email.subType) || (this.thread && this.thread.subType) || '').toLowerCase();
    }

    private get referrerName(): string {
        return (this.email && this.email.referrerName) || (this.thread && this.thread.referrerName) || '';
    }

    private get referrerEmail(): string {
        return (this.email && this.email.referrerEmail) || (this.thread && this.thread.referrerEmail) || '';
    }

    /**
     * Which metadata line the summary card shows (Story 4): a form submission reads "Submitted {date}"
     * (never message/participant counts), a single-message email reads "Received {date}", and a real
     * multi-message thread keeps the counts + last-activity line.
     */
    get metaMode(): 'form' | 'single' | 'multi' {
        if (this.subType === 'form') {
            return 'form';
        }
        return this.messages.length === 1 ? 'single' : 'multi';
    }

    /** Received/submitted date of the linked email - drives "Submitted/Received {date}" on the meta line. */
    get receivedDate(): Date | undefined {
        const d = this.email && this.email.receivedDate;
        return d ? new Date(d as any) : undefined;
    }

    /**
     * Quiet sub-type token on the metadata line (Story 4): 'Form' (+ referred by {name}) for form
     * submissions, 'Referral · via {name|email}' for referrals, and NO chip ('') for direct/unknown
     * sub-types. Exhaustive over the persisted values - nothing else may blank the referral info.
     */
    get subTypeChipLabel(): string {
        switch (this.subType) {
            case 'form':
                return this.referrerName ? 'Form · referred by ' + this.referrerName : 'Form';
            case 'referral':
                return this.referrerName || this.referrerEmail
                    ? 'Referral · via ' + (this.referrerName || this.referrerEmail)
                    : 'Referral';
            case 'direct':
            case '':
            default:
                return '';
        }
    }

    /** "alice@x.com +2 more" — the recipients summary on a message's sub-line. */
    toSummary(m: ThreadMessageDto): string {
        const to = (m && m.to) || [];
        if (!to.length) {
            return '(no recipients)';
        }
        return to.length === 1 ? to[0] : to[0] + ' +' + (to.length - 1) + ' more';
    }

    detailsLabel(m: ThreadMessageDto): string {
        return this.isDetailsOpen(m) ? 'Hide' : 'Details';
    }

    /**
     * Text for the "saved to folder" row, reflecting what was ACTUALLY filed. Attachment saving is
     * decoupled from Save Emails: with it on, the .eml body is filed (filedEmailBody=true) alongside any
     * attachments; with it off, only the attachments are filed. So:
     *   Email + N attachment(s) saved to {folder}  (body + attachments)
     *   Email saved to {folder}                    (body only)
     *   N attachment(s) saved to {folder}          (attachments only - Save Emails off)
     *   Saved to {folder}                          (fallback)
     */
    filedLabel(m: ThreadMessageDto): string {
        const folder = (m && m.filedFolderName) || '';
        const count = (m && m.attachments && m.attachments.length) || 0;
        const emailSaved = !!(m && m.filedEmailBody);
        const attachments = count + (count === 1 ? ' attachment' : ' attachments');
        if (emailSaved && count > 0) {
            return 'Email + ' + attachments + ' saved to ' + folder;
        }
        if (emailSaved) {
            return 'Email saved to ' + folder;
        }
        if (count > 0) {
            return attachments + ' saved to ' + folder;
        }
        return 'Saved to ' + folder;
    }

    /** "Received in x@y" / "Sent from x@y" — the Inbox line in a message's Details. */
    inboxLine(m: ThreadMessageDto): string {
        const addr = this.inboxAddress;
        if (!addr) {
            return '';
        }
        return (m.isSent ? 'Sent from ' : 'Received in ') + addr;
    }

    toggleMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.menuOpen = !this.menuOpen;
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        // Clicks on the caret / menu items / the wait-menu (and its date-picker popover) call
        // stopPropagation, so this only fires for outside clicks.
        if (this.menuOpen) {
            this.menuOpen = false;
            this._cdr.markForCheck();
        }
        if (this.waitMenuOpen) {
            this.waitMenuOpen = false;
            this._cdr.markForCheck();
        }
    }

    onReply(mode: EmailReplyMode, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        this.menuOpen = false;
        if (this.email) {
            this.reply.emit({ mode, email: this.email, inboxAddress: this.inboxAddress });
        }
    }

    viewOriginal(): void {
        this.menuOpen = false;
        this.showOriginal = true;
        // Load once per task; reused on re-open. Fetched live (never stored), rendered in a sandboxed iframe.
        if (this.taskId && this.originalLoadedForTaskId !== this.taskId) {
            this.loadOriginal(this.taskId);
        }
    }

    closeOriginal(): void {
        this.showOriginal = false;
    }

    openInOutlook(): void {
        this.menuOpen = false;
        if (this.email && this.email.webLink) {
            window.open(this.email.webLink, '_blank');
        }
    }

    private ensureBodyDoc(m: ThreadMessageDto): void {
        if (!m.id || this.bodyHtml[m.id]) {
            return;
        }
        // pad:false — the in-thread body has no internal padding so it left-aligns with the sender name/To line
        // (the .email-thread__bodywrap padding-left handles indentation under the avatar); margin-top adds top gap.
        this.bodyHtml[m.id] = this._sanitizer.bypassSecurityTrustHtml(this.buildDoc(m.body, m.isHtml, false));
    }

    /**
     * Auto-size a message iframe to its content so the thread panel scrolls (never the frame, which would show an
     * inner scrollbar). Safe because the frame has allow-same-origin WITHOUT allow-scripts: no email JS runs, but the
     * parent may read the framed document height. Re-measures after late-loading images settle.
     */
    onFrameLoad(event: Event): void {
        const iframe = event.target as HTMLIFrameElement;
        let doc: Document | null = null;
        let imgs: HTMLCollectionOf<HTMLImageElement> | null = null;
        let img: HTMLImageElement;
        let i: number;

        this.resizeFrame(iframe);
        try {
            doc = iframe.contentDocument || (iframe.contentWindow ? iframe.contentWindow.document : null);
        } catch {
            doc = null;
        }
        imgs = doc && doc.images ? doc.images : null;
        if (imgs) {
            for (i = 0; i < imgs.length; i++) {
                img = imgs[i];
                if (!img.complete) {
                    img.addEventListener('load', () => this.resizeFrame(iframe));
                    img.addEventListener('error', () => this.resizeFrame(iframe));
                }
            }
        }
    }

    private resizeFrame(iframe: HTMLIFrameElement): void {
        let doc: Document | null = null;
        let height = 0;

        try {
            doc = iframe.contentDocument || (iframe.contentWindow ? iframe.contentWindow.document : null);
        } catch {
            doc = null;
        }
        if (!doc || !doc.body) {
            return;
        }
        height = Math.max(
            doc.body.scrollHeight,
            doc.body.offsetHeight,
            doc.documentElement ? doc.documentElement.scrollHeight : 0
        );
        if (height > 0) {
            iframe.style.height = height + 'px';
        }
    }

    private loadOriginal(id: number): void {
        this.originalLoading = true;
        this.originalError = '';
        this._emailService.getOriginalEmail(id, null).subscribe(
            res => {
                this.originalLoading = false;
                const data = res ? res.responseData : undefined;
                if (!data || (res && res.isError)) {
                    this.originalError = (res && res.errorMessage) || 'Could not load the original email.';
                    this._cdr.markForCheck();
                    return;
                }
                this.originalHtml = this._sanitizer.bypassSecurityTrustHtml(this.buildDoc(data.body, data.isHtml));
                this.originalLoadedForTaskId = id;
                this._cdr.markForCheck();
            },
            () => {
                this.originalLoading = false;
                this.originalError = 'Could not load the original email.';
                this._cdr.markForCheck();
            }
        );
    }

    /**
     * Wrap an email body in a minimal HTML document for an iframe srcdoc. Used by TWO frames:
     *   - the in-thread message frame: sandbox="allow-same-origin allow-popups ..." (NO allow-scripts) — no email JS
     *     runs, but the parent CAN read contentDocument to auto-size the frame (see onFrameLoad/resizeFrame);
     *   - the "Original email" overlay frame: sandbox="allow-popups ..." (no allow-same-origin) — fully origin-isolated,
     *     fixed height, so it needs no height measurement.
     * In BOTH, allow-scripts is omitted, which is what neutralizes <script>/inline handlers/javascript: URLs.
     * WARNING: never add allow-scripts while allow-same-origin is present — that combination lets the framed
     * content strip its own sandbox and reach the parent app. <base target="_blank"> + allow-popups opens links
     * in a new tab.
     */
    private buildDoc(body: string | undefined, isHtml: boolean | undefined, pad: boolean = true): string {
        const raw = body || '';
        const padPx = pad ? 12 : 0;
        const inner = isHtml
            ? raw
            : '<pre style="white-space:pre-wrap;word-wrap:break-word;margin:0;">' + this.escapeHtml(raw) + '</pre>';
        return '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">'
            + '<style>html,body{margin:0;padding:' + padPx + 'px;background:transparent;color:#111;'
            + 'font:14px/1.5 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;}'
            + 'img{max-width:100%;height:auto;}table{max-width:100%;}</style></head><body>'
            + inner + '</body></html>';
    }

    private escapeHtml(s: string): string {
        return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * Re-fetch the linked email + thread for the current task WITHOUT resetting the surface's open/expanded
     * state. Called by the host when a real-time email update (a client reply reopened this task) arrives via
     * SignalR while the task is open, so the newest message appears without a manual refresh.
     */
    public reloadEmail(): void {
        if (this.taskId) {
            this.load();
        }
    }

    private load(): void {
        const id = this.taskId!;
        this.loading = true;
        this._emailService.getTaskEmail(id, null).subscribe(
            res => {
                this.loading = false;
                this.email = res ? res.responseData : undefined;
                this.hasEmailChange.emit(this.hasEmail);
                this._cdr.markForCheck();
                // Only fetch the full thread once we know the task actually has a linked email.
                if (this.hasEmail) {
                    this.loadThread(id);
                }
            },
            () => {
                this.loading = false;
                this.email = undefined;
                this.hasEmailChange.emit(false);
                this._cdr.markForCheck();
            }
        );
    }

    private loadThread(id: number, isMore: boolean = false): void {
        if (isMore) {
            this.loadingMore = true;
        } else {
            // Fresh task -> reset the window to the newest 15.
            this.threadTop = 15;
            this.threadLoading = true;
            this.threadError = '';
        }
        this._emailService.getTaskThread(id, this.threadTop, true, null).subscribe(
            res => {
                this.threadLoading = false;
                this.loadingMore = false;
                const data = res ? res.responseData : undefined;
                this.thread = data;
                if (res && res.isError) {
                    this.threadError = res.errorMessage || 'Could not load the conversation.';
                }
                // Prefer the server's full-thread AI summary (sec 18.1); fall back to the create-time notes
                // summary when the AI was unavailable so the card is never blank on a real email task.
                this.summaryText = (data && data.aiThreadSummary) || this.extractSummary(this.taskNotes);
                // Every message shows its full body (no collapse / "show message").
                this.messages.forEach(m => this.ensureBodyDoc(m));
                this.rebuildFollowUpVms();
                this._cdr.markForCheck();
            },
            () => {
                this.threadLoading = false;
                this.loadingMore = false;
                if (!isMore) {
                    this.threadError = 'Could not load the conversation.';
                }
                this._cdr.markForCheck();
            }
        );
    }

    // --- Story 6: no-reply silence events (thread banner + chips) ---------------------------------

    /**
     * Rebuild the thread's follow-up view-models from thread.followUpEvents: the single active banner, the
     * per-message chips (anchored above the outbound each guards), plus the "Follow-up" and "New reply"
     * message-badge sets. Called after every thread (re)load - the template only reads the precomputed
     * fields/maps (no per-CD allocation).
     */
    private rebuildFollowUpVms(): void {
        this.activeBanner = undefined;
        this.eventChipsByMsgId = {};
        this.followUpMsgIds = new Set<string>();
        this.newReplyMsgId = undefined;

        const msgs = this.messages;
        const events = (this.thread && this.thread.followUpEvents) || [];

        // "Follow-up" badge: a Sent message whose chronologically-previous (older, i+1 newest-first) message
        // is also Sent = a follow-up sent with no reply in between.
        for (let i = 0; i < msgs.length; i++) {
            const older = msgs[i + 1];
            if (msgs[i].isSent && older && older.isSent && msgs[i].id) {
                this.followUpMsgIds.add(msgs[i].id!);
            }
        }

        // "New reply" tag (T15): after a fire, a client reply takes the top slot. Tag the newest message
        // when it is inbound AND some fired event resolved as 'reply'.
        const hasReplyResolved = events.some(e => (e.resolvedReason || '') === 'reply');
        if (hasReplyResolved && msgs.length && !msgs[0].isSent && msgs[0].id) {
            this.newReplyMsgId = msgs[0].id;
        }

        const waitingOn = this.waitingOnName;

        for (let idx = 0; idx < events.length; idx++) {
            const e = events[idx];
            const anchorMs = e.anchorSentAtUtc ? new Date(e.anchorSentAtUtc).getTime()
                : (e.firedAtUtc ? new Date(e.firedAtUtc).getTime() : 0);
            const days = anchorMs ? Math.max(0, moment().diff(moment(anchorMs), 'days')) : 0;
            const isActive = (e.state || '') === 'active' && !!e.firedAtUtc;
            const reason = e.resolvedReason || '';
            const sentDate = e.anchorSentAtUtc || e.firedAtUtc;

            let suffix = '';
            if (reason === 'dismissed') {
                suffix = ' · dismissed';
            } else if (reason === 'followed_up') {
                const on = e.resolvedAtUtc || e.anchorSentAtUtc;
                suffix = on ? ' · followed up ' + moment(on).format('MMM D') : ' · followed up';
            } else if (reason === 'superseded') {
                // "Confirm & complete" re-armed a new watcher over this fired one - mark it as history.
                suffix = ' · rescheduled';
            }

            const vm: FollowUpEventVm = {
                id: e.firedAtUtc || ('event-' + idx),
                isActive,
                chipText: days + (days === 1 ? ' day, no reply' : ' days, no reply'),
                chipSuffix: suffix,
                bannerTitle: 'No reply from ' + waitingOn,
                bannerSubtitle: 'Your last email was sent ' + (sentDate ? moment(sentDate).format('MMM D') : 'recently')
                    + ' — ' + days + (days === 1 ? ' day ago.' : ' days ago.'),
                anchorMessageId: this.matchAnchorMessageId(e.anchorSentAtUtc)
            };

            if (isActive) {
                this.activeBanner = vm;
            }
            if (vm.anchorMessageId) {
                (this.eventChipsByMsgId[vm.anchorMessageId] = this.eventChipsByMsgId[vm.anchorMessageId] || []).push(vm);
            }
        }
    }

    /** Match an event's anchor outbound to a displayed message (closest by time); defaults to the newest
     *  message when there is no anchor, and undefined only when the thread has no messages. */
    private matchAnchorMessageId(anchorUtc: string | undefined): string | undefined {
        const msgs = this.messages;
        if (!msgs.length) {
            return undefined;
        }
        if (!anchorUtc) {
            return msgs[0].id;
        }
        const anchorMs = new Date(anchorUtc).getTime();
        let bestId = msgs[0].id;
        let bestDiff = Number.MAX_VALUE;
        for (const m of msgs) {
            if (!m.date) { continue; }
            const diff = Math.abs(new Date(m.date as any).getTime() - anchorMs);
            if (diff < bestDiff) { bestDiff = diff; bestId = m.id; }
        }
        return bestId;
    }

    /** The person we're waiting on to reply - the newest RECEIVED sender, else the original sender. */
    get waitingOnName(): string {
        const received = this.messages.find(m => !m.isSent);
        const name = (received && (received.fromName || received.from)) || this.senderName;
        return name || 'the client';
    }

    isFollowUpMsg(m: ThreadMessageDto): boolean {
        return !!(m && m.id && this.followUpMsgIds.has(m.id));
    }

    isNewReplyMsg(m: ThreadMessageDto): boolean {
        return !!(m && m.id && this.newReplyMsgId === m.id);
    }

    trackByEvent(index: number, e: FollowUpEventVm): string {
        return e ? e.id : String(index);
    }

    /** "Draft follow-up": open the reply composer (Reply) with a follow-up NUDGE - the composer pre-loads
     *  the nudge into its Draft-with-AI popup so one tap writes the chase (spec Story 6). */
    draftFollowUp(): void {
        this.menuOpen = false;
        if (this.email) {
            this.reply.emit({ mode: 'reply', email: this.email, inboxAddress: this.inboxAddress, nudge: true });
        }
    }

    toggleWaitMenu(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        this.waitMenuOpen = !this.waitMenuOpen;
        if (this.waitMenuOpen) {
            // Never show the preset menu and the custom date input at once; recompute the date labels against
            // TODAY (they're built at construction, so a modal opened across midnight would show stale dates).
            this.waitCustomOpen = false;
            this.refreshWaitPresetVms();
        }
    }

    /** Rebuild the Wait preset date labels + custom-picker min against the current day. */
    private refreshWaitPresetVms(): void {
        this.waitMinDate = moment().startOf('day');
        this.waitPresetVms = this.waitPresets.map(p => ({
            label: p.label,
            value: p.value,
            dateLabel: moment().add(p.days, 'days').format('MMM D')
        }));
    }

    trackByWaitPreset(index: number, p: { value: string }): string {
        return p ? p.value : String(index);
    }

    /** The first name of the person we're waiting on, for the confirm card ("We'll wait for Karen's reply"). */
    get waitingOnFirstName(): string {
        const n = this.waitingOnName;
        if (!n || n === 'the client') {
            return 'the client';
        }
        return n.split(/\s+/)[0];
    }

    /** Pick a relative preset from the Wait menu -> SNOOZE immediately (no confirm step). */
    pickWait(value: string): void {
        this.snoozeFollowUp('preset', value);
    }

    /** "Custom date" menu row - closes the menu and reveals the inline date picker in the actions row. */
    chooseCustom(): void {
        this.waitMenuOpen = false;
        this.waitCustomOpen = true;
        this._cdr.markForCheck();
    }

    /** Pick a custom absolute date from the inline picker -> SNOOZE immediately to that date. */
    pickWaitDate(date: moment.Moment | undefined): void {
        if (!date || !date.isValid()) {
            return;
        }
        this.snoozeFollowUp('date', date.format('YYYY-MM-DD'));
    }

    /**
     * Wait-dropdown snooze: reschedule the SAME fired watcher to re-fire in X days (ExtendTaskFollowUp keeps
     * FiredAt, so the banner/chip persist and re-fire once at the new date) AND, on the backend, move the
     * task's Start + Due to today + X. No re-completion. The banner refreshes in place; the moved task dates
     * take the task off today's My Day and bring it back when the wait elapses (reflected on the next My Day
     * load / when the task modal closes).
     */
    private snoozeFollowUp(kind: string, value: string): void {
        if (!this.taskId || this.followUpSaving) {
            return;
        }
        // Human "returns on" date for the toast: a preset uses its pre-formatted label ("Jul 25"), a custom
        // pick formats the chosen date.
        const whenLabel = kind === 'date'
            ? moment(value, 'YYYY-MM-DD').format('MMM D')
            : (this.waitPresetVms.find(p => p.value === value) || { dateLabel: '' }).dateLabel;
        const input = new ExtendTaskFollowUpInput();
        input.taskId = this.taskId;
        input.timingKind = kind;
        input.timingValue = value;
        this.waitSelected = value;
        this.waitMenuOpen = false;
        this.waitCustomOpen = false;
        this.followUpSaving = true;
        this._emailService.extendTaskFollowUp(input).subscribe(
            res => {
                this.followUpSaving = false;
                // extendTaskFollowUp returns the wrapped 200-with-isError convention (business failures don't
                // throw), so a real snooze failure lands HERE with res.isError=true — guard it (mirrors the grid
                // snooze + confirmAndComplete) or we'd falsely toast success AND close the modal on a no-op.
                if (res && res.isError) {
                    this._toastr.error(res.errorMessage || 'Could not snooze the follow-up. Please try again.', null, this.toastrOptions);
                    this._cdr.markForCheck();
                    return;
                }
                this.refreshFollowUpEventsSilent();
                this._toastr.success(whenLabel ? ('Follow-up snoozed to ' + whenLabel + '.') : 'Follow-up snoozed.', null, this.toastrOptions);
                // Close the task (host closes the modal) so the snoozed follow-up leaves the view — consistent
                // with the grid Snooze, which drops the row off Today. The watcher + task dates are already updated.
                this.followUpSnoozed.emit();
            },
            () => {
                this.followUpSaving = false;
                this._toastr.error('Could not snooze the follow-up. Please try again.', null, this.toastrOptions);
                this._cdr.markForCheck();
            }
        );
    }

    /** "Change" on the confirm card - back to the amber alert (the pick is kept so the menu shows it checked). */
    backToAlert(): void {
        this.waitConfirmMode = false;
        this.waitCustomOpen = false;
        this._cdr.markForCheck();
    }

    /** "Confirm & complete": RE-ARM the watcher with the picked Wait timing (supersedes the fired row with a
     *  fresh armed-not-fired one, so the completed task shows the "Watching for a reply" card - not the amber
     *  fired banner), and ONLY once that succeeds ask the host to complete the task via its EXISTING mark-
     *  complete path. Completing without a successful arm would silently break the "reopens by {date}" promise,
     *  so a failed arm surfaces an error and leaves the task open to retry. */
    confirmAndComplete(): void {
        if (!this.taskId || this.followUpSaving) {
            return;
        }
        let kind = 'preset';
        let value = this.waitSelected;
        if (this.waitSelected === 'custom' && this.waitCustomDate) {
            kind = 'date';
            value = this.waitCustomDate.format('YYYY-MM-DD');
        }
        if (!value) {
            return; // nothing picked - the confirm card shouldn't be reachable without a pick, but guard anyway
        }
        const input = new ArmTaskFollowUpInput();
        input.taskId = this.taskId;
        input.timingKind = kind;
        input.timingValue = value;
        input.origin = 'user';
        this.followUpSaving = true;
        this._emailService.armTaskFollowUp(input).subscribe(
            res => {
                this.followUpSaving = false;
                // Treat both an explicit error AND a silent no-op arm (e.g. the hard gate returned unarmed
                // because the task has no linked client) as failure - completing on a broken "reopens by X"
                // promise would be worse than blocking. The user can still complete via the normal path.
                if (!res || res.isError || !res.responseData || !res.responseData.armed) {
                    abp.notify.error((res && res.errorMessage) || 'Could not set the follow-up. Please try again.');
                    this._cdr.markForCheck();
                    return;
                }
                this.waitConfirmMode = false;
                // Rebuild off the new state so the amber banner doesn't linger stale if the host completion
                // then fails (modal stays open); on success the modal closes and this GET is a harmless no-op.
                this.refreshFollowUpEventsSilent();
                this.completeTask.emit();
            },
            () => {
                this.followUpSaving = false;
                abp.notify.error('Could not set the follow-up. Please try again.');
                this._cdr.markForCheck();
            }
        );
    }

    /**
     * §8.4 "Wait 3 more days": re-arm the SAME fired watcher 3 days from NOW, in place. ExtendTaskFollowUp
     * reschedules the fired row WITHOUT clearing FiredAt, so the banner + chip persist and update their day
     * count in place at the next fire — no confirm step, no re-completing the task, and it never stacks a
     * second banner (spec 8.4). Mirrors the dismiss flow's silent refresh.
     */
    waitThreeMoreDays(): void {
        if (!this.taskId || this.followUpSaving) {
            return;
        }
        const input = new ExtendTaskFollowUpInput();
        input.taskId = this.taskId;
        input.timingKind = 'preset';
        input.timingValue = '3d';
        this.followUpSaving = true;
        this._emailService.extendTaskFollowUp(input).subscribe(
            () => { this.followUpSaving = false; this.refreshFollowUpEventsSilent(); },
            () => { this.followUpSaving = false; this._cdr.markForCheck(); }
        );
    }

    /** "Dismiss": cancel the watcher ('dismissed'); the banner collapses to a chip suffixed "· dismissed". */
    dismissFollowUp(): void {
        if (!this.taskId || this.followUpSaving) {
            return;
        }
        const input = new CancelTaskFollowUpInput();
        input.taskId = this.taskId;
        input.reason = 'dismissed';
        this.followUpSaving = true;
        this._emailService.cancelTaskFollowUp(input).subscribe(
            () => { this.followUpSaving = false; this.refreshFollowUpEventsSilent(); },
            () => { this.followUpSaving = false; this._cdr.markForCheck(); }
        );
    }

    /** Re-fetch the thread WITHOUT the loading flash and rebuild the follow-up view-models (after Wait/Dismiss). */
    private refreshFollowUpEventsSilent(): void {
        if (!this.taskId) {
            return;
        }
        this._emailService.getTaskThread(this.taskId, this.threadTop, true, null).subscribe(
            res => {
                const data = res ? res.responseData : undefined;
                if (data && !(res && res.isError)) {
                    this.thread = data;
                    this.messages.forEach(m => this.ensureBodyDoc(m));
                    this.rebuildFollowUpVms();
                }
                this._cdr.markForCheck();
            },
            () => { this._cdr.markForCheck(); }
        );
    }

    /** Reveal the earlier messages (sec 18.2). The thread is small, so one click loads the rest of it. */
    loadEarlier(): void {
        if (!this.taskId || this.loadingMore) {
            return;
        }
        this.threadTop = (this.thread && this.thread.totalCount) || (this.threadTop + 25);
        this.loadThread(this.taskId, true);
    }

    /** A short "1.2 MB" label for an attachment chip. */
    attSize(bytes: number | undefined): string {
        const b = bytes || 0;
        if (b <= 0) { return ''; }
        if (b < 1024) { return b + ' B'; }
        if (b < 1024 * 1024) { return (b / 1024).toFixed(0) + ' KB'; }
        return (b / (1024 * 1024)).toFixed(1) + ' MB';
    }

    /**
     * Open an attachment in-app (sec 18.3): fetch its bytes on demand (owner-scoped) and open the blob. Only a
     * safe whitelist (images + PDF) is previewed inline via the browser's native viewer ("not SharePoint");
     * EVERYTHING else is force-downloaded (a.download) so attacker-controlled bytes - html/svg/xhtml - can NEVER
     * render as active content in the app's own origin. Too large / fetch error -> the filed folder (or a toast).
     */
    openAttachment(m: ThreadMessageDto, att: AttachmentDto): void {
        if (!this.taskId || !m || !m.id || !att || !att.id || this.attLoadingId) {
            return;
        }
        this.attLoadingId = att.id;

        this._emailService.getAttachmentContent(this.taskId, m.id, att.id, null).subscribe(
            res => {
                const data = res ? res.responseData : undefined;
                this.attLoadingId = undefined;
                this._cdr.markForCheck();
                if (!data || (res && res.isError) || data.tooLarge || !data.contentBase64) {
                    // Too big / unavailable -> the filed folder that holds the original, else a message.
                    this.attachmentFallback();
                    return;
                }
                const contentType = data.contentType || 'application/octet-stream';
                const baseType = contentType.split(';')[0].trim().toLowerCase();
                const blob = this.base64ToBlob(data.contentBase64, contentType);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                if (TaskEmailBarComponent.InlinePreviewTypes.indexOf(baseType) >= 0) {
                    // Safe to preview inline (browser-native image / PDF viewer).
                    a.target = '_blank';
                    a.rel = 'noopener';
                } else {
                    // Force a download for everything else - never render untrusted bytes in the app origin.
                    a.download = data.fileName || att.name || 'attachment';
                }
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            },
            () => {
                this.attLoadingId = undefined;
                this._cdr.markForCheck();
                this.attachmentFallback();
            }
        );
    }

    private attachmentFallback(): void {
        if (this.canOpenFolder) {
            this.openFolder();
        } else {
            abp.notify.warn('This attachment could not be opened.');
        }
    }

    private base64ToBlob(base64: string, contentType: string): Blob {
        const bytes = atob(base64);
        const len = bytes.length;
        const arr = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            arr[i] = bytes.charCodeAt(i);
        }
        return new Blob([arr], { type: contentType });
    }
}
